/**
 * The ONE details form (docs-company ADR-110 Amendments 6 and 8). "Leave your
 * details" and a booking's `collect_details` draw this same component: first
 * name, last name, email and phone, in the same order, labelled, marked,
 * limited and checked by the same rules (details.ts), each refusal drawn
 * against the box it names. The two name boxes are one wire value, joined by
 * details.ts, so the first name the assistant greets a visitor by is the one
 * they typed rather than the first word of whatever they typed. Only what the
 * form is FOR differs:
 *
 *   lead     around the contact fields, the form the business set (ADR-114
 *            Am.2) — its message and its own questions — and a honeypot.
 *            The parent POSTs /chat/lead and maps the outcome.
 *   booking  exactly the fields the server listed — since Amendment 6 only
 *            the ones the conversation does not already hold, so a visitor
 *            who left their details is not handed them again. The values
 *            ride the next /chat/message turn as `details`, gated like any
 *            other message; this form posts nowhere of its own.
 *
 * Until Amendment 6 these were two forms, LeadForm and BookingDetailsForm,
 * and they drifted: the lead's phone read "Phone (optional)" where the
 * booking's read "Phone *", the two took different email lengths, and only
 * the booking form put a refusal beside its field.
 *
 * 🚨 THE MESSAGE BELONGS TO THE FIELD, NOT TO THE FORM.
 *
 * The booking form's message was once one `<p>` after the whole field loop,
 * so "Please enter a valid email address" was drawn under the LAST field —
 * 71px below the email box, under the phone. A visitor typed their first
 * name, pressed Enter, was moved to the email field by the focus call below
 * without seeing why, and carried on typing: their SURNAME went into the
 * email box, and the only sentence about it sat beside the phone number. It
 * reached preprod and was reported from the console's own preview.
 *
 * So a refusal is rendered INSIDE the offending field's group, under its
 * input, where `focus()` has just put the caret, and each field owns the id
 * its input's `aria-describedby` names. `tests/widget.spec.ts` pins the
 * geometry, not just the text. Only a failure of the send itself (the lead
 * cap, a network error) is the form's, and stands above the buttons.
 */
import { useEffect, useId, useMemo, useRef, useState, type ClipboardEvent, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { bookingContact, validateBookingDetails, type BookingDetails, type DetailField } from './actions';
import { CONTACT_FIELDS, CONTACT_LIMITS, type ContactDetail, type ContactField, type ContactValues } from './details';
import { fieldSentence } from './fieldMessages';
import { counterFrom, countryOptions, dialCodeFor, phoneInputMax, stripOwnCode, wholePaste } from './fields';
import {
  DEFAULT_LEAD_FORM,
  LEAD_FIELD_LIMITS,
  LEAD_LIMITS,
  leadContact,
  validateLead,
  type LeadFormConfig,
  type LeadInput,
} from './lead';
import { fill, type WidgetStrings } from './strings';
import { honeypotStyle, type Styles } from './theme';

interface CommonProps {
  ui: WidgetStrings;
  styles: Styles;
  brandColor: string;
  /** The page's language: the country list and the phone sentences name countries in it. */
  language?: string;
  /** The country the phone box starts on (fields.ts `defaultPhoneCountry`). */
  phoneCountry?: string;
  /** Closes the form and leaves the conversation as it is: typing still works. */
  onCancel: () => void;
}

export interface LeadDetailsProps extends CommonProps {
  purpose: 'lead';
  /** What the business's form asks; today's form when the server has not said. */
  form?: LeadFormConfig;
  /**
   * Sends the validated input, with the first name beside it for the words
   * the visitor reads back. Resolves to an error message to show above the
   * buttons, or a refusal naming the box the backend refused (drawn under
   * that box, with the caret in it) — the form stays open, values kept, for
   * a retry — or null when the parent has handled the outcome and closed it.
   */
  onSubmit: (input: LeadInput, firstName: string, drawn: readonly string[]) => Promise<string | Refusal | null>;
}

export interface BookingDetailsProps extends CommonProps {
  purpose: 'booking';
  /** The fields to show, in the server's order. */
  fields: readonly DetailField[];
  /** The fields marked and checked as required: a subset of `fields`. */
  required: readonly DetailField[];
  /**
   * Sends the validated details as the next turn, with the first name for the
   * visitor's own bubble; the parent then clears the turn's actions and this
   * form goes. Returns false when nothing was sent, so the form stays usable.
   */
  onSubmit: (details: BookingDetails, firstName: string) => boolean;
}

export type DetailsFormProps = LeadDetailsProps | BookingDetailsProps;

const CONTACT_INPUT: Record<ContactField, { type: string; inputMode: 'text' | 'email' | 'tel'; autoComplete: string }> = {
  firstName: { type: 'text', inputMode: 'text', autoComplete: 'given-name' },
  lastName: { type: 'text', inputMode: 'text', autoComplete: 'family-name' },
  email: { type: 'email', inputMode: 'email', autoComplete: 'email' },
  // The NATIONAL number (1.31.0): the country is chosen beside the box, and a
  // browser filling a whole '+44 …' into a box sized for a national number
  // would be cut at its maxLength into a different number.
  phone: { type: 'tel', inputMode: 'tel', autoComplete: 'tel-national' },
};

/**
 * The two name boxes share a line where there is room for them: they are one
 * answer, and stacking them puts the email a scroll below the fold on a phone
 * in landscape. Below 320px of form the row wraps to two lines on its own.
 */
const PAIRED: readonly ContactField[] = ['firstName', 'lastName'];

/** A refusal: the field it names — '' for the send as a whole — and its sentence. */
export interface Refusal {
  field: string;
  text: string;
}

type Focusable = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export function DetailsForm(props: DetailsFormProps) {
  const { ui, styles, brandColor, onCancel } = props;
  const language = props.language ?? 'en';
  const leadForm = props.purpose === 'lead' ? (props.form ?? DEFAULT_LEAD_FORM) : null;
  const contact =
    props.purpose === 'lead' ? leadContact(props.form ?? DEFAULT_LEAD_FORM) : bookingContact(props.fields, props.required);

  const [values, setValues] = useState<Record<ContactField, string>>(() =>
    Object.fromEntries(CONTACT_FIELDS.map((field) => [field, ''])) as Record<ContactField, string>,
  );
  const [message, setMessage] = useState('');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [website, setWebsite] = useState('');
  // The phone's country (1.31.0): the box holds the national number, and the
  // country beside it decides how it is read — the server reads it the same.
  const [country, setCountry] = useState(() => (dialCodeFor(props.phoneCountry ?? '') ? (props.phoneCountry as string).toUpperCase() : 'GB'));
  const countries = useMemo(() => countryOptions(language), [language]);
  const [error, setError] = useState<Refusal | null>(null);
  const [busy, setBusy] = useState(false);
  const inputs = useRef<Record<string, Focusable | null>>({});
  const id = useId();

  useEffect(() => {
    inputs.current[contact.fields[0]]?.focus();
    // Once, on mount: each form that opens is a fresh one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contactLabels: Record<ContactField, string> = {
    firstName: ui.detailsFirstName,
    lastName: ui.detailsLastName,
    email: ui.detailsEmail,
    phone: ui.detailsPhone,
  };

  /** What a box holds, and its limit: for "this is {count}" and "up to {max}". */
  const lengthOf = (field: string): number =>
    field === 'message' ? message.length : field in values ? values[field as ContactField].length : (answers[field] ?? '').length;
  const limitOf = (field: string): number => {
    if (field === 'message') return LEAD_LIMITS.message;
    if (field === 'phone') return phoneInputMax(country);
    if (field in CONTACT_LIMITS) return CONTACT_LIMITS[field as ContactField];
    const extra = leadForm?.extraFields.find((f) => f.id === field);
    return extra ? LEAD_FIELD_LIMITS[extra.type] : 0;
  };
  const labelOf = (field: string): string =>
    field in contactLabels
      ? contactLabels[field as ContactField]
      : field === 'message'
        ? ui.leadMessageLabel
        : leadForm?.extraFields.find((f) => f.id === field)?.label ?? '';

  /** The sentence for a refused field, in the visitor's language (fieldMessages.ts). */
  const messageFor = (field: string, reason: 'required' | 'invalid' | 'tooLong', detail?: ContactDetail): string =>
    fieldSentence(
      { ui, language, label: labelOf, extraFields: leadForm?.extraFields, length: lengthOf, limit: limitOf },
      field,
      reason,
      detail,
    );

  const refuse = (field: string, reason: 'required' | 'invalid' | 'tooLong', detail?: ContactDetail) => {
    setError({ field, text: messageFor(field, reason, detail) });
    inputs.current[field]?.focus();
  };

  /**
   * A paste that would pass a box's limit is taken WHOLE and refused in words
   * (shared-audit#17): the browser would cut it at `maxLength` and say
   * nothing, and a cut email or phone is a DIFFERENT one.
   */
  const pasteWhole = (field: string, max: number, current: string, set: (next: string) => void) =>
    (event: ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const target = event.currentTarget;
      const whole = wholePaste(current, target.selectionStart, target.selectionEnd, event.clipboardData.getData('text'), max);
      if (whole === null) return;
      event.preventDefault();
      // A pasted '+44 …' is the chosen country's code in front of a number
      // that fits once it is dropped: that is not a paste past the limit.
      const next = field === 'phone' ? stripOwnCode(whole, country) : whole;
      set(next);
      if (next.length > max) {
        setError({ field, text: fill(ui.textTooLong, { label: labelOf(field), max: String(max), count: String(next.length) }) });
      } else {
        touched(field);
      }
    };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const given: ContactValues = { ...values };
    if (props.purpose === 'lead') {
      const v = validateLead({ ...given, message, fields: answers, website }, props.form ?? DEFAULT_LEAD_FORM, country);
      if (!v.ok) {
        refuse(v.field, v.reason, v.detail);
        return;
      }
      setError(null);
      setBusy(true);
      try {
        const drawn = [
          ...contact.fields,
          ...(leadForm && leadForm.message !== 'hidden' ? ['message'] : []),
          ...(leadForm?.extraFields.map((f) => f.id) ?? []),
        ];
        const failure = await props.onSubmit(v.value, v.firstName, drawn);
        if (typeof failure === 'string') setError({ field: '', text: failure });
        else if (failure) {
          setError(failure);
          inputs.current[failure.field]?.focus();
        }
      } finally {
        setBusy(false);
      }
      return;
    }
    const v = validateBookingDetails(given, props.fields, props.required, country);
    if (!v.ok) {
      refuse(v.field, v.reason, v.detail);
      return;
    }
    setError(null);
    setBusy(true);
    if (!props.onSubmit(v.value, v.firstName)) setBusy(false);
  };

  const labelStyle: CSSProperties = { display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', margin: '8px 0 4px' };
  const card: CSSProperties = {
    ...styles.assistantBubble,
    alignSelf: 'stretch',
    maxWidth: '100%',
    padding: '12px 14px',
  };
  const primary: CSSProperties = {
    ...styles.chip,
    background: brandColor,
    borderColor: brandColor,
    color: styles.ink,
    opacity: busy ? 0.7 : 1,
    cursor: busy ? 'default' : 'pointer',
  };
  const textarea: CSSProperties = { ...styles.formField, resize: 'vertical', minHeight: '64px' };
  const titleId = `${id}-title`;

  /** A required field is its label and a visual `*` (`required` carries it); an optional one says so in words. */
  const label = (text: string, required: boolean): ReactNode =>
    required ? (
      <>
        {text}
        <span aria-hidden="true"> *</span>
      </>
    ) : (
      fill(ui.detailsOptional, { label: text })
    );

  /**
   * The contact boxes in the order they are drawn, the two name boxes paired
   * onto one line. A form that draws only one of them (a booking that already
   * holds the other half is impossible today, but the server lists the fields)
   * puts it on a line of its own.
   */
  const rows: ContactField[][] = [];
  for (const field of contact.fields) {
    const last = rows[rows.length - 1];
    if (last && last.length === 1 && PAIRED.includes(field) && PAIRED.includes(last[0])) last.push(field);
    else rows.push([field]);
  }

  const errorId = (field: string) => `${id}-${field}-error`;
  const countId = (field: string) => `${id}-${field}-count`;
  /** What ties an input to its refusal while it has one, and to its counter while one shows. */
  const described = (field: string, counted = false) => {
    const invalid = error?.field === field;
    const ids = [invalid ? errorId(field) : '', counted ? countId(field) : ''].filter(Boolean).join(' ');
    return { 'aria-invalid': invalid || undefined, 'aria-describedby': ids || undefined };
  };
  /** "n / max", from 90 % of the limit (shared-audit#17 › Free text). */
  const counter = (field: string, length: number, max: number) =>
    length >= counterFrom(max) ? (
      <p id={countId(field)} style={{ margin: '2px 0 0', fontSize: '11px', textAlign: 'right', color: length > max ? '#b91c1c' : '#6b7280' }} data-pwe={`count-${field}`}>
        {length} / {max}
      </p>
    ) : null;
  const alertFor = (field: string) =>
    error?.field === field ? (
      <p id={errorId(field)} role="alert" style={styles.formError}>
        {error.text}
      </p>
    ) : null;
  /**
   * The message goes the moment its own field is touched: a red line under a
   * box the visitor has just corrected is about text that no longer exists. A
   * message about ANOTHER field stays — it is still true.
   */
  const touched = (field: string) => setError((prev) => (prev?.field === field ? null : prev));
  const keep = (field: string) => (el: Focusable | null) => {
    inputs.current[field] = el;
  };
  const setAnswer = (fieldId: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [fieldId]: value }));
    touched(fieldId);
  };

  return (
    // noValidate: `required` and the input types stay for their semantics,
    // but the messages come from details.ts + strings.ts (the backends'
    // rules, in the visitor's language) rather than the browser's own bubble
    // in the browser's language.
    <form
      onSubmit={submit}
      style={card}
      aria-labelledby={titleId}
      aria-busy={busy}
      noValidate
      data-pwe={props.purpose === 'lead' ? 'lead-form' : 'booking-details-form'}
      data-pwe-details={props.purpose}
    >
      <div id={titleId} style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
        {props.purpose === 'lead' ? ui.leadTitle : ui.bookingDetailsTitle}
      </div>

      {rows.map((row) => {
        const boxes = row.map((field) => {
          const required = contact.required.includes(field);
          const attrs = CONTACT_INPUT[field];
          const max = limitOf(field);
          const set = (next: string) => {
            // A '+44' pasted or typed in front of a UK number is the chosen
            // country's own code: the box is for the national number.
            const value = field === 'phone' ? stripOwnCode(next, country) : next;
            setValues((prev) => ({ ...prev, [field]: value }));
          };
          const input = (
            <input
              id={`${id}-${field}`}
              ref={keep(field)}
              name={field}
              type={attrs.type}
              inputMode={attrs.inputMode}
              autoComplete={attrs.autoComplete}
              value={values[field]}
              onChange={(e) => {
                set(e.target.value);
                touched(field);
              }}
              onPaste={pasteWhole(field, max, values[field], set)}
              maxLength={max}
              required={required}
              {...described(field)}
              style={field === 'phone' ? { ...styles.formField, flex: '1 1 auto', minWidth: 0 } : styles.formField}
            />
          );
          return (
            <div key={field} style={row.length > 1 ? { flex: '1 1 120px', minWidth: 0 } : undefined} data-pwe={`details-field-${field}`}>
              <label htmlFor={`${id}-${field}`} style={labelStyle}>
                {label(contactLabels[field], required)}
              </label>
              {field === 'phone' ? (
                <div style={{ display: 'flex', gap: '6px' }}>
                  {/* The number's country, beside it: how the number is read, and
                      the code it is sent with. Named for assistive tech; the
                      visible label is the phone's. */}
                  <select
                    aria-label={ui.detailsCountry}
                    value={country}
                    onChange={(e) => {
                      setCountry(e.target.value);
                      touched('phone');
                    }}
                    style={{ ...styles.formField, flex: '0 1 42%', minWidth: 0 }}
                    data-pwe="details-phone-country"
                  >
                    {countries.map((c) => (
                      <option key={c.iso} value={c.iso}>
                        {c.name} (+{c.dial})
                      </option>
                    ))}
                  </select>
                  {input}
                </div>
              ) : (
                input
              )}
              {alertFor(field)}
            </div>
          );
        });
        return row.length > 1 ? (
          <div key={row.join('-')} style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {boxes}
          </div>
        ) : (
          boxes
        );
      })}

      {leadForm && leadForm.message !== 'hidden' && (
        <div data-pwe="details-field-message">
          <label htmlFor={`${id}-message`} style={labelStyle}>
            {label(ui.leadMessageLabel, leadForm.message === 'required')}
          </label>
          <textarea
            id={`${id}-message`}
            ref={keep('message')}
            name="message"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              touched('message');
            }}
            onPaste={pasteWhole('message', LEAD_LIMITS.message, message, setMessage)}
            maxLength={LEAD_LIMITS.message}
            rows={3}
            required={leadForm.message === 'required'}
            {...described('message', message.length >= counterFrom(LEAD_LIMITS.message))}
            style={textarea}
          />
          {counter('message', message.length, LEAD_LIMITS.message)}
          {alertFor('message')}
        </div>
      )}

      {leadForm?.extraFields.map((field) => {
        const fieldId = `${id}-${field.id}`;
        const value = answers[field.id] ?? '';
        return (
          <div key={field.id} data-pwe-field={field.id}>
            <label htmlFor={fieldId} style={labelStyle}>
              {label(field.label, field.required)}
            </label>
            {field.type === 'select' ? (
              <select
                id={fieldId}
                ref={keep(field.id)}
                name={field.id}
                value={value}
                onChange={(e) => setAnswer(field.id, e.target.value)}
                required={field.required}
                {...described(field.id)}
                style={styles.formField}
              >
                <option value="">{ui.leadChoose}</option>
                {field.options.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                id={fieldId}
                ref={keep(field.id)}
                name={field.id}
                value={value}
                onChange={(e) => setAnswer(field.id, e.target.value)}
                onPaste={pasteWhole(field.id, LEAD_FIELD_LIMITS.textarea, value, (next) => setAnswers((prev) => ({ ...prev, [field.id]: next })))}
                maxLength={LEAD_FIELD_LIMITS.textarea}
                rows={3}
                required={field.required}
                {...described(field.id)}
                style={textarea}
              />
            ) : (
              <input
                id={fieldId}
                ref={keep(field.id)}
                name={field.id}
                value={value}
                onChange={(e) => setAnswer(field.id, e.target.value)}
                onPaste={pasteWhole(field.id, LEAD_FIELD_LIMITS.text, value, (next) => setAnswers((prev) => ({ ...prev, [field.id]: next })))}
                maxLength={LEAD_FIELD_LIMITS.text}
                required={field.required}
                {...described(field.id)}
                style={styles.formField}
              />
            )}
            {alertFor(field.id)}
          </div>
        );
      })}

      {/* Honeypot, lead only: off-screen, out of the tab order, hidden from
          assistive tech — and NOT display:none, which is what a form-filling
          bot checks for. A person never sees it, so it is always ''. A
          booking's details ride a chat turn, gated like any other. */}
      {leadForm && (
        <div style={honeypotStyle} aria-hidden="true">
          <label htmlFor={`${id}-website`}>Website</label>
          <input
            id={`${id}-website`}
            name="website"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>
      )}

      {alertFor('')}

      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
        <button type="button" onClick={onCancel} style={styles.chip} disabled={busy}>
          {ui.leadCancel}
        </button>
        <button type="submit" style={primary} disabled={busy}>
          {busy ? ui.leadSending : props.purpose === 'lead' ? ui.leadSubmit : ui.bookingContinue}
        </button>
      </div>
    </form>
  );
}
